
export interface SessionLock {
  id: string;
  operation: string;
  timestamp: number;
  sessionId: string;
  expires: number;
}

export class SessionLockManager {
  private static LOCK_KEY = 'app_operation_locks';
  private static LOCK_TIMEOUT = 30000; // 30 segundos
  private sessionId: string;
  private channel: BroadcastChannel;

  constructor() {
    this.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.channel = new BroadcastChannel('data_management_sync');
    this.setupCleanup();
  }

  private setupCleanup() {
    // Limpar locks expirados a cada 5 segundos
    setInterval(() => {
      this.cleanExpiredLocks();
    }, 5000);

    // Limpar locks ao fechar a aba
    window.addEventListener('beforeunload', () => {
      this.releaseAllLocks();
    });
  }

  async acquireLock(operation: string): Promise<boolean> {
    const lockId = `${operation}_${this.sessionId}`;
    const now = Date.now();
    const expires = now + SessionLockManager.LOCK_TIMEOUT;

    try {
      const locks = this.getCurrentLocks();
      
      // Verificar se já existe um lock ativo para esta operação
      const existingLock = locks.find(l => 
        l.operation === operation && 
        l.expires > now && 
        l.sessionId !== this.sessionId
      );

      if (existingLock) {
        console.log(`Lock já existe para operação ${operation} por sessão ${existingLock.sessionId}`);
        return false;
      }

      // Criar novo lock
      const newLock: SessionLock = {
        id: lockId,
        operation,
        timestamp: now,
        sessionId: this.sessionId,
        expires
      };

      // Remover locks antigos desta sessão para a mesma operação
      const filteredLocks = locks.filter(l => 
        !(l.operation === operation && l.sessionId === this.sessionId)
      );

      const updatedLocks = [...filteredLocks, newLock];
      localStorage.setItem(SessionLockManager.LOCK_KEY, JSON.stringify(updatedLocks));

      // Notificar outras abas
      this.channel.postMessage({
        type: 'lock_acquired',
        operation,
        sessionId: this.sessionId
      });

      console.log(`Lock adquirido para operação ${operation}`);
      return true;
    } catch (error) {
      console.error('Erro ao adquirir lock:', error);
      return false;
    }
  }

  releaseLock(operation: string): void {
    try {
      const locks = this.getCurrentLocks();
      const filteredLocks = locks.filter(l => 
        !(l.operation === operation && l.sessionId === this.sessionId)
      );

      localStorage.setItem(SessionLockManager.LOCK_KEY, JSON.stringify(filteredLocks));

      // Notificar outras abas
      this.channel.postMessage({
        type: 'lock_released',
        operation,
        sessionId: this.sessionId
      });

      console.log(`Lock liberado para operação ${operation}`);
    } catch (error) {
      console.error('Erro ao liberar lock:', error);
    }
  }

  private getCurrentLocks(): SessionLock[] {
    try {
      const stored = localStorage.getItem(SessionLockManager.LOCK_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  private cleanExpiredLocks(): void {
    try {
      const locks = this.getCurrentLocks();
      const now = Date.now();
      const validLocks = locks.filter(l => l.expires > now);
      
      if (validLocks.length !== locks.length) {
        localStorage.setItem(SessionLockManager.LOCK_KEY, JSON.stringify(validLocks));
      }
    } catch (error) {
      console.error('Erro ao limpar locks expirados:', error);
    }
  }

  private releaseAllLocks(): void {
    try {
      const locks = this.getCurrentLocks();
      const filteredLocks = locks.filter(l => l.sessionId !== this.sessionId);
      localStorage.setItem(SessionLockManager.LOCK_KEY, JSON.stringify(filteredLocks));
    } catch (error) {
      console.error('Erro ao liberar todos os locks:', error);
    }
  }

  isLocked(operation: string): boolean {
    const locks = this.getCurrentLocks();
    const now = Date.now();
    return locks.some(l => 
      l.operation === operation && 
      l.expires > now && 
      l.sessionId !== this.sessionId
    );
  }
}
